import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getMinhaAtividade } from '../../../../../app/modules/helpers/Api';
import { useEmpresaSelecionada } from './useEmpresaSelecionada';
import { KTSVG } from '../../../../helpers';

interface Contagem {
    rotulo: string;
    valor: number;
}

interface Resumo {
    nao_conformidade: { respondidos: number };
    analise_de_riscos: { aguardando_resposta: number; concluidos: number };
    requerimentos: { em_atendimento: number; finalizados: number };
}

interface Card {
    titulo: string;
    descricao: string;
    destino: string;
    icone: string;
    contagens: Contagem[];
}

const RESUMO_VAZIO: Resumo = {
    nao_conformidade: { respondidos: 0 },
    analise_de_riscos: { aguardando_resposta: 0, concluidos: 0 },
    requerimentos: { em_atendimento: 0, finalizados: 0 },
};

/**
 * Painel da tela inicial para quem nao tem acesso aos graficos.
 *
 * Todas as contagens sao do usuario autenticado na empresa em contexto.
 * Nenhuma e da empresa inteira: agregado de empresa e o que o gate de papel
 * remove desses usuarios.
 */
const PainelMinhasAtividades: React.FC = () => {
    const { empresas, empresaId, selecionar, carregando } = useEmpresaSelecionada();
    const [resumo, setResumo] = useState<Resumo>(RESUMO_VAZIO);
    const [carregandoResumo, setCarregandoResumo] = useState(false);
    const [erro, setErro] = useState(false);

    useEffect(() => {
        if (empresaId === null) {
            return;
        }

        setErro(false);
        setCarregandoResumo(true);

        getMinhaAtividade(empresaId)
            .then((response) => setResumo(response.data.data || RESUMO_VAZIO))
            .catch(() => setErro(true))
            .finally(() => setCarregandoResumo(false));
    }, [empresaId]);

    if (carregando) {
        return null;
    }

    if (empresas.length === 0) {
        return (
            <div className='container bg-white p-5 rounded-4 w-90 mt-5'>
                <div className='alert alert-info d-flex align-items-center p-5 mb-0'>
                    <KTSVG
                        path='/media/icons/duotune/general/gen044.svg'
                        className='svg-icon-info svg-icon-2hx me-5'
                    />
                    <div className='d-flex flex-column'>
                        <h5 className='mb-1'>Nenhuma empresa vinculada</h5>
                        <span className='fs-6'>
                            Seu usuário ainda não está vinculado a nenhuma empresa. Procure o
                            responsável pela sua conta para receber o vínculo.
                        </span>
                    </div>
                </div>
            </div>
        );
    }

    const cards: Card[] = [
        {
            titulo: 'Não conformidade',
            descricao: 'Formulários de não conformidade que você respondeu nesta empresa.',
            destino: '/crafted/pages/commum/calculator',
            icone: '/media/icons/duotune/finance/fin007.svg',
            contagens: [
                { rotulo: 'Respondidos por mim', valor: resumo.nao_conformidade.respondidos },
            ],
        },
        {
            titulo: 'Análise de riscos',
            descricao: 'Análises de risco que você enviou. Quem responde recebe o link por e-mail.',
            destino: '/crafted/pages/commum/gar',
            icone: '/media/icons/duotune/graphs/gra007.svg',
            contagens: [
                { rotulo: 'Aguardando resposta', valor: resumo.analise_de_riscos.aguardando_resposta },
                { rotulo: 'Concluídas', valor: resumo.analise_de_riscos.concluidos },
            ],
        },
        {
            titulo: 'Requerimentos',
            descricao: 'Requerimentos de titular e denúncias que você atendeu nesta empresa.',
            destino: '/crafted/pages/commum/requeriments',
            icone: '/media/icons/duotune/communication/com002.svg',
            contagens: [
                { rotulo: 'Em atendimento por mim', valor: resumo.requerimentos.em_atendimento },
                { rotulo: 'Finalizados por mim', valor: resumo.requerimentos.finalizados },
            ],
        },
    ];

    return (
        <div className='container bg-white p-5 rounded-4 w-90 mt-5'>
            {empresas.length > 1 && (
                <div className='row mb-5'>
                    <div className='col-md-4'>
                        <label className='form-label fw-bold text-dark fs-6'>Empresa</label>
                        <select
                            className='form-control form-control-lg form-control-solid'
                            value={empresaId === null ? '' : String(empresaId)}
                            onChange={(e) => selecionar(Number(e.target.value))}
                        >
                            {empresas.map((empresa) => (
                                <option key={empresa.value} value={empresa.value}>
                                    {empresa.text}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
            )}

            {erro && (
                <div className='alert alert-warning p-4'>
                    Não foi possível carregar suas contagens. Os atalhos abaixo continuam válidos.
                </div>
            )}

            <div className='row'>
                {cards.map((card) => (
                    <div className='col-md-4 mb-5' key={card.titulo}>
                        <Link to={card.destino} className='text-decoration-none'>
                            <div className='card h-100 border border-gray-300 bg-hover-light'>
                                <div className='card-body d-flex flex-column'>
                                    <KTSVG path={card.icone} className='svg-icon-2hx svg-icon-primary mb-3' />

                                    <h4 className='text-dark fw-bolder mb-2'>{card.titulo}</h4>

                                    <p className='text-gray-600 fs-7 flex-grow-1'>{card.descricao}</p>

                                    {card.contagens.map((contagem) => (
                                        <div
                                            className='d-flex justify-content-between align-items-center border-top pt-3 mt-3'
                                            key={contagem.rotulo}
                                        >
                                            <span className='text-gray-700 fs-7'>{contagem.rotulo}</span>
                                            <span className='text-dark fw-bolder fs-3'>
                                                {carregandoResumo ? '—' : contagem.valor}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </Link>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default PainelMinhasAtividades;
